import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, LayoutDashboard, Clock3, CalendarDays, Timer, Wallet, Users, UserRound,
  UsersRound, CheckCheck, Building2, Settings2, IndianRupee, Bell, LogOut, Menu, X,
} from "lucide-react";
import { papi, portalLogout } from "./portalApi";
import { hasPerm, fmtDate, fmtTime } from "./ui";
import PortalLogin from "./PortalLogin";
import Dashboard from "./pages/Dashboard";
import Attendance from "./pages/Attendance";
import Leave from "./pages/Leave";
import Timesheets from "./pages/Timesheets";
import Payslips from "./pages/Payslips";
import Directory from "./pages/Directory";
import Profile from "./pages/Profile";
import MyTeam from "./pages/MyTeam";
import Approvals from "./pages/Approvals";
import Employees from "./pages/Employees";
import HRAdmin from "./pages/HRAdmin";
import PayrollAdmin from "./pages/PayrollAdmin";
import Reports from "./pages/Reports";

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

const anyPerm = (emp, perms) => perms.some((p) => hasPerm(emp, p));

function navGroups(emp) {
  const groups = [
    {
      title: "Workspace",
      items: [
        { to: "/portal", end: true, label: "Dashboard", icon: LayoutDashboard, tid: "dashboard" },
        { to: "/portal/attendance", label: "Attendance", icon: Clock3, tid: "attendance" },
        { to: "/portal/leave", label: "Leave", icon: CalendarDays, tid: "leave" },
        { to: "/portal/timesheets", label: "Timesheets", icon: Timer, tid: "timesheets" },
        { to: "/portal/payslips", label: "Payslips", icon: Wallet, tid: "payslips" },
        { to: "/portal/directory", label: "Directory", icon: Users, tid: "directory" },
        { to: "/portal/profile", label: "My Profile", icon: UserRound, tid: "profile" },
      ],
    },
  ];
  const team = [];
  if (hasPerm(emp, "team_view")) team.push({ to: "/portal/team", label: "My Team", icon: UsersRound, tid: "my-team" });
  if (anyPerm(emp, ["approve", "final_approve"])) team.push({ to: "/portal/approvals", label: "Approvals", icon: CheckCheck, tid: "approvals" });
  if (team.length) groups.push({ title: "Team", items: team });
  if (hasPerm(emp, "hr_view")) {
    groups.push({
      title: "HR",
      items: [
        { to: "/portal/employees", label: "Employees", icon: Building2, tid: "employees" },
        { to: "/portal/hr", label: "HR Admin", icon: Settings2, tid: "hr-admin" },
      ],
    });
  }
  if (hasPerm(emp, "employees_manage")) {
    groups.push({ title: "Reports", items: [{ to: "/portal/reports", label: "Day Reports", icon: ClipboardList, tid: "reports" }] });
  }
  if (hasPerm(emp, "payroll_view")) {
    groups.push({ title: "Finance", items: [{ to: "/portal/payroll", label: "Payroll", icon: IndianRupee, tid: "payroll" }] });
  }
  return groups;
}

const initials = (name = "") =>
  name.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export default function PortalApp() {
  const [emp, setEmp] = useState(undefined); // undefined = loading, null = signed out
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem("rcs_portal_token")) {
      setEmp(null);
      return;
    }
    try {
      const r = await papi.get("/me");
      setEmp(r.data);
    } catch {
      portalLogout();
      setEmp(null);
    }
  }, []);

  const loadNotifs = useCallback(async () => {
    try {
      const r = await papi.get("/notifications");
      setNotifs(r.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { setSidebarOpen(false); setNotifOpen(false); }, [location.pathname]);
  useEffect(() => { if (emp) loadNotifs(); }, [emp, loadNotifs]);

  const [approvalCount, setApprovalCount] = useState(0);
  const loadApprovalCount = useCallback(async () => {
    if (!emp || !(hasPerm(emp, "approve") || hasPerm(emp, "final_approve"))) return;
    try {
      const r = await papi.get("/approvals/count");
      setApprovalCount(r.data.count || 0);
    } catch { /* ignore */ }
  }, [emp]);

  useEffect(() => {
    loadApprovalCount();
    const t = setInterval(loadApprovalCount, 60000);
    return () => clearInterval(t);
  }, [loadApprovalCount, location.pathname]);

  const markAllRead = async () => {
    try {
      await papi.post("/notifications/read-all");
      loadNotifs();
    } catch { /* ignore */ }
  };

  const signOut = () => {
    portalLogout();
    setEmp(null);
    toast.success("Signed out.");
  };

  if (emp === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950" data-testid="portal-boot">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" aria-label="Loading portal" />
      </div>
    );
  }
  if (emp === null) return <PortalLogin onSuccess={loadMe} />;

  const unread = notifs.filter((n) => !n.read).length;
  const groups = navGroups(emp);
  const ctx = { emp, refreshEmp: loadMe };

  return (
    <PortalCtx.Provider value={ctx}>
      <div className="min-h-screen bg-ivory" data-testid="portal-app">
        <aside
          data-testid="portal-sidebar"
          className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-navy-950 text-slate-300 transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
            <div className="flex items-center gap-3">
              <img src="/rcs-logo.png" alt="Roy Career Solutions" className="h-10 w-auto" />
              <div>
                <p className="font-serif text-sm leading-tight text-ivory">Employee Portal</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Roy Career Solutions</p>
              </div>
            </div>
            <button
              className="text-slate-400 transition-colors hover:text-gold lg:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
              data-testid="portal-sidebar-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 py-5" aria-label="Portal navigation">
            {groups.map((g) => (
              <div key={g.title} className="mb-6">
                <p className="px-3 pb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-gold/70">{g.title}</p>
                <div className="space-y-1">
                  {g.items.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      data-testid={`portal-nav-${it.tid}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                          isActive ? "bg-gold/10 text-gold" : "text-slate-400 hover:bg-white/5 hover:text-ivory"
                        }`
                      }
                    >
                      <it.icon className="h-4 w-4" aria-hidden /> {it.label}
                      {it.tid === "approvals" && approvalCount > 0 && (
                        <span
                          data-testid="approvals-count-badge"
                          className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white"
                        >
                          {approvalCount}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="border-t border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-gold/10 font-serif text-sm text-gold">
                {initials(emp.fullName)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ivory" data-testid="portal-user-name">{emp.fullName}</p>
                <p className="truncate text-[11px] text-gold/80">{emp.roleLabel}</p>
              </div>
              <button
                onClick={signOut}
                data-testid="portal-sign-out"
                aria-label="Sign out"
                className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-white/5 hover:text-gold"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 bg-navy-950/60 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        <div className="lg:pl-72">
          <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-navy-900/10 bg-ivory/85 px-4 py-3.5 backdrop-blur-xl sm:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <button
                className="rounded-lg p-2 text-navy-900 transition-colors hover:bg-navy-900/5 lg:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
                data-testid="portal-menu-open"
              >
                <Menu className="h-5 w-5" />
              </button>
              <p className="truncate text-sm font-semibold text-navy-900">
                {emp.designation} <span className="font-normal text-slate-400">· {emp.department} · {emp.brand}</span>
              </p>
            </div>
            <div className="relative">
              <button
                onClick={() => {
                  setNotifOpen((v) => !v);
                  if (!notifOpen) loadNotifs();
                }}
                data-testid="portal-notifications-btn"
                aria-label="Notifications"
                className="relative rounded-full border border-navy-900/10 bg-white p-2.5 text-navy-900 transition-colors hover:border-gold hover:text-gold-dark"
              >
                <Bell className="h-4 w-4" />
                {unread > 0 && (
                  <span
                    data-testid="portal-notifications-badge"
                    className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-[10px] font-bold text-navy-950"
                  >
                    {unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div
                  className="absolute right-0 mt-3 w-96 max-w-[calc(100vw-2rem)] rounded-2xl border border-navy-900/10 bg-white p-4 shadow-2xl"
                  data-testid="portal-notifications-panel"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <p className="font-serif text-base text-navy-900">Notifications</p>
                    <button
                      onClick={markAllRead}
                      data-testid="portal-notifications-read-all"
                      className="text-xs font-semibold text-gold-dark transition-colors hover:text-navy-900"
                    >
                      Mark all read
                    </button>
                  </div>
                  <div className="max-h-80 space-y-2 overflow-y-auto">
                    {notifs.length === 0 && (
                      <p className="py-6 text-center text-sm text-slate-400" data-testid="portal-notifications-empty">
                        No notifications yet.
                      </p>
                    )}
                    {notifs.map((n) => (
                      <div
                        key={n.id}
                        className={`rounded-xl border p-3 ${n.read ? "border-navy-900/5 bg-white" : "border-gold/30 bg-gold/5"}`}
                      >
                        <p className="text-sm font-semibold text-navy-900">{n.title}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{n.body}</p>
                        <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                          {fmtDate(n.createdAt)} · {fmtTime(n.createdAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </header>

          <main className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="leave" element={<Leave />} />
              <Route path="timesheets" element={<Timesheets />} />
              <Route path="payslips" element={<Payslips />} />
              <Route path="directory" element={<Directory />} />
              <Route path="profile" element={<Profile />} />
              <Route path="team" element={hasPerm(emp, "team_view") ? <MyTeam /> : <Navigate to="/portal" replace />} />
              <Route path="approvals" element={anyPerm(emp, ["approve", "final_approve"]) ? <Approvals /> : <Navigate to="/portal" replace />} />
              <Route path="employees" element={hasPerm(emp, "hr_view") ? <Employees /> : <Navigate to="/portal" replace />} />
              <Route path="hr" element={hasPerm(emp, "hr_view") ? <HRAdmin /> : <Navigate to="/portal" replace />} />
              <Route path="payroll" element={hasPerm(emp, "payroll_view") ? <PayrollAdmin /> : <Navigate to="/portal" replace />} />
              <Route path="reports" element={hasPerm(emp, "employees_manage") ? <Reports /> : <Navigate to="/portal" replace />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </PortalCtx.Provider>
  );
}
